import { type FormEvent, useState } from 'react';
import { Link } from 'react-router';
import { type AuditEntry, api, type DashboardSession, type TeamDetails } from '../api';
import { PlusIcon, UsersIcon } from '../components/icons';
import { TwoFactorCard } from '../components/TwoFactorCard';
import {
  Copyable,
  Dialog,
  ErrorNote,
  formatDate,
  formatDateTime,
  PageHeader,
  RowMenu,
  Status,
  timeAgo,
  useToast,
} from '../components/ui';
import { useLoad } from '../hooks';
import { useSession } from '../session';

export function Settings() {
  const { me, team } = useSession();
  const canEdit = team.role !== 'member';
  const { data, error, reload } = useLoad(() => api<TeamDetails>('GET', '/team'), []);
  const [inviting, setInviting] = useState(false);
  const [removing, setRemoving] = useState<{ userId: string; email: string } | null>(null);
  const toast = useToast();

  return (
    <>
      <PageHeader
        title="Settings"
        description="Your team, its members, its limits and its audit log."
      />
      <ErrorNote error={error} />
      {data ? (
        <>
          <div className="card">
            <div className="card-head">
              <h2>Team</h2>
            </div>
            <div className="card-body">
              <dl className="facts" style={{ gridTemplateColumns: '140px minmax(0, 1fr)' }}>
                <dt>Name</dt>
                <dd>{data.name}</dd>
                <dt>Team ID</dt>
                <dd>
                  <Copyable value={data.id} />
                </dd>
                <dt>Plan</dt>
                <dd>
                  {data.plan.charAt(0).toUpperCase() + data.plan.slice(1)} ·{' '}
                  <Link className="link" to="/billing">
                    Billing
                  </Link>
                </dd>
                <dt>Daily limit</dt>
                <dd>
                  {data.dailyLimit.toLocaleString('en-IN')} recipients a day · resets at midnight
                  IST
                </dd>
                <dt>Sending</dt>
                <dd>
                  <Status
                    status={data.status === 'active' ? 'Active' : data.status}
                    tone={data.status === 'active' ? 'ok' : 'warn'}
                  />
                </dd>
                <dt>Data region</dt>
                <dd>India · AWS Mumbai (ap-south-1)</dd>
                <dt>Created</dt>
                <dd>{formatDate(data.createdAt)}</dd>
              </dl>
            </div>
          </div>

          <div className="card" style={{ overflow: 'hidden' }}>
            <div className="card-head">
              <div>
                <h2>Members</h2>
                <p>
                  Owners and admins manage domains, keys and members. Members can view everything.
                </p>
              </div>
              {canEdit ? (
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => setInviting(true)}
                >
                  <PlusIcon />
                  Add member
                </button>
              ) : null}
            </div>
            <div className="table-wrap" style={{ padding: '12px 12px 4px' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Member</th>
                    <th>Role</th>
                    <th className="nowrap hide-sm">Joined</th>
                    <th style={{ width: 48 }} />
                  </tr>
                </thead>
                <tbody>
                  {(data.members ?? []).map((m) => (
                    <tr key={m.userId}>
                      <td>
                        <div className="cell-main">
                          <span className="row-icon">
                            <UsersIcon />
                          </span>
                          <span className="text">
                            <strong>{m.name || m.email}</strong>
                            {m.name ? <span className="sub">{m.email}</span> : null}
                          </span>
                          {m.userId === me.user.id ? <span className="chip">You</span> : null}
                        </div>
                      </td>
                      <td>
                        <Status status={m.role} tone={m.role === 'owner' ? 'accent' : ''} />
                      </td>
                      <td className="nowrap hide-sm muted">{formatDate(m.joinedAt)}</td>
                      <td className="right">
                        {canEdit && m.role !== 'owner' ? (
                          <RowMenu
                            items={[
                              {
                                label: m.userId === me.user.id ? 'Leave team' : 'Remove from team',
                                danger: true,
                                onSelect: () => setRemoving({ userId: m.userId, email: m.email }),
                              },
                            ]}
                          />
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : null}

      <TwoFactorCard />

      <Sessions />

      <AuditLog />

      {inviting ? (
        <AddMember
          onClose={() => setInviting(false)}
          onAdded={(email) => {
            setInviting(false);
            toast.show(`${email} added. They can log in with their email.`);
            reload();
          }}
        />
      ) : null}
      {removing ? (
        <Dialog
          title={`Remove ${removing.email}?`}
          description="They lose access to this team right away. Their API keys keep working until you revoke them."
          onClose={() => setRemoving(null)}
          footer={
            <>
              <button type="button" className="btn btn-ghost" onClick={() => setRemoving(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={async () => {
                  const member = removing;
                  setRemoving(null);
                  try {
                    await api('DELETE', `/team/members/${encodeURIComponent(member.userId)}`);
                    toast.show(`${member.email} removed`);
                    reload();
                  } catch (err) {
                    toast.show(err instanceof Error ? err.message : 'Could not remove');
                  }
                }}
              >
                Remove
              </button>
            </>
          }
        />
      ) : null}
      {toast.node}
    </>
  );
}

function AddMember({
  onClose,
  onAdded,
}: {
  onClose: () => void;
  onAdded: (email: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'member' | 'admin'>('member');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function submit(e?: FormEvent) {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api('POST', '/team/members', { email: email.trim(), role });
      onAdded(email.trim());
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  }

  return (
    <Dialog
      title="Add a team member"
      description="They log in with this email address; no invitation link is needed."
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            form="add-member"
            className="btn btn-primary"
            disabled={busy || !email.trim()}
          >
            {busy ? 'Adding…' : 'Add member'}
          </button>
        </>
      }
    >
      <form id="add-member" onSubmit={submit} style={{ display: 'contents' }}>
        <div className="field">
          <label htmlFor="member-email">Email</label>
          <input
            id="member-email"
            className="input"
            type="email"
            placeholder="colleague@yourcompany.in"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="radio-cards">
          <label>
            <input
              type="radio"
              name="role"
              checked={role === 'member'}
              onChange={() => setRole('member')}
            />
            <strong>Member</strong>
            <span>Can view emails, domains and settings.</span>
          </label>
          <label>
            <input
              type="radio"
              name="role"
              checked={role === 'admin'}
              onChange={() => setRole('admin')}
            />
            <strong>Admin</strong>
            <span>Can also manage domains, keys, webhooks and members.</span>
          </label>
        </div>
        <ErrorNote error={error} />
      </form>
    </Dialog>
  );
}

/** A short, human label for a browser's user agent, such as "Chrome on macOS". */
export function deviceLabel(userAgent: string | null): string {
  if (!userAgent) return 'Unknown device';
  const ua = userAgent;
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /OPR\/|Opera/.test(ua)
      ? 'Opera'
      : /Firefox\//.test(ua)
        ? 'Firefox'
        : /Chrome\//.test(ua)
          ? 'Chrome'
          : /Safari\//.test(ua)
            ? 'Safari'
            : 'Browser';
  const os = /iPhone|iPad/.test(ua)
    ? 'iOS'
    : /Android/.test(ua)
      ? 'Android'
      : /Mac OS X/.test(ua)
        ? 'macOS'
        : /Windows/.test(ua)
          ? 'Windows'
          : /Linux/.test(ua)
            ? 'Linux'
            : '';
  return os ? `${browser} on ${os}` : browser;
}

/** The current user's dashboard logins, with a way to end the ones they don't recognise. */
function Sessions() {
  const { data, error, reload } = useLoad(
    () => api<{ data: DashboardSession[] }>('GET', '/auth/sessions'),
    [],
  );
  const [confirmOthers, setConfirmOthers] = useState(false);
  const toast = useToast();
  const sessions = data?.data ?? [];
  const others = sessions.filter((s) => !s.current).length;

  async function revoke(id: string) {
    try {
      await api('DELETE', `/auth/sessions/${encodeURIComponent(id)}`);
      toast.show('Session logged out');
      reload();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'Could not log out that session');
    }
  }

  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <div className="card-head">
        <div>
          <h2>Active sessions</h2>
          <p>
            Where you're logged in to the dashboard. Sessions end after 7 days without use, and 30
            days after login at the latest.
          </p>
        </div>
        {others > 0 ? (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setConfirmOthers(true)}
          >
            Log out all other sessions
          </button>
        ) : null}
      </div>
      <ErrorNote error={error} />
      <div className="table-wrap" style={{ padding: '12px 12px 4px' }}>
        <table className="table">
          <thead>
            <tr>
              <th>Device</th>
              <th className="nowrap hide-sm">IP address</th>
              <th className="nowrap">Last active</th>
              <th className="nowrap hide-sm">Logged in</th>
              <th style={{ width: 48 }} />
            </tr>
          </thead>
          <tbody>
            {sessions.map((s) => (
              <tr key={s.id}>
                <td>
                  <div className="cell-main">
                    <span className="text">
                      <strong>{deviceLabel(s.userAgent)}</strong>
                    </span>
                    {s.current ? <span className="chip">This browser</span> : null}
                  </div>
                </td>
                <td className="nowrap hide-sm mono muted">{s.ip ?? '—'}</td>
                <td className="nowrap muted">{s.current ? 'Now' : timeAgo(s.lastSeenAt)}</td>
                <td className="nowrap hide-sm muted">{formatDate(s.createdAt)}</td>
                <td className="right">
                  {s.current ? null : (
                    <RowMenu
                      items={[{ label: 'Log out', danger: true, onSelect: () => revoke(s.id) }]}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {confirmOthers ? (
        <Dialog
          title="Log out all other sessions?"
          description={`This ends ${others} other session${others === 1 ? '' : 's'}. This browser stays logged in. API keys are not affected.`}
          onClose={() => setConfirmOthers(false)}
          footer={
            <>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setConfirmOthers(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={async () => {
                  setConfirmOthers(false);
                  try {
                    const res = await api<{ revoked: number }>(
                      'POST',
                      '/auth/sessions/revoke-others',
                    );
                    toast.show(`${res.revoked} session${res.revoked === 1 ? '' : 's'} logged out`);
                    reload();
                  } catch (err) {
                    toast.show(err instanceof Error ? err.message : 'Could not log out sessions');
                  }
                }}
              >
                Log out others
              </button>
            </>
          }
        />
      ) : null}
      {toast.node}
    </div>
  );
}

/** What an audit entry says happened, such as "Created API key Production". */
export function describeAudit(entry: AuditEntry): string {
  const target = entry.targetName ? ` ${entry.targetName}` : '';
  const d = entry.details;
  switch (entry.action) {
    case 'api_key.created':
      return `Created ${d.scope === 'full' ? 'a full-access' : 'a sending-only'} API key${target}`;
    case 'api_key.revoked':
      return `Revoked API key${target}`;
    case 'domain.added':
      return `Added domain${target}`;
    case 'domain.deleted':
      return `Deleted domain${target}`;
    case 'team.member_added':
      return `Added${target || ' a member'} as ${d.role === 'admin' ? 'an admin' : 'a member'}`;
    case 'team.member_removed':
      return `Removed${target || ' a member'} from the team`;
    case 'team.onboarded':
      return 'Completed onboarding';
    case 'team.limit_raised':
      return `Raised the daily limit from ${Number(d.from).toLocaleString('en-IN')} to ${Number(d.to).toLocaleString('en-IN')}`;
    case 'team.auto_paused':
      return 'Paused sending after a high bounce or complaint rate';
    case 'webhook.created':
      return `Added webhook ${String(d.url ?? '')}`.trim();
    case 'webhook.updated':
      return 'Updated a webhook';
    case 'webhook.deleted':
      return 'Deleted a webhook';
    case 'webhook.secret_rotated':
      return 'Rotated a webhook signing secret';
    case 'webhook.auto_disabled':
      return `Disabled webhook ${String(d.url ?? '')} after repeated failures`.trim();
    case 'email.canceled':
      return 'Canceled a scheduled email';
    case 'billing.details_updated':
      return 'Updated billing details';
    case 'billing.checkout_started':
      return `Started checkout for the ${String(d.plan ?? '')} plan`;
    case 'billing.plan_changed':
      return `Changed plan from ${String(d.from ?? '')} to ${String(d.to ?? '')}`;
    case 'billing.cancelled':
      return d.atPeriodEnd ? 'Cancelled the plan at the end of the period' : 'Cancelled the plan';
    default:
      return entry.action;
  }
}

function actorLabel(entry: AuditEntry): string {
  if (entry.actor.type === 'vaartix') return 'Vaartix';
  if (entry.actor.type === 'api_key') return 'An API key';
  return entry.actor.name || entry.actor.email || 'A former member';
}

/** Who changed what in the team: keys, domains, members, webhooks and billing. */
function AuditLog() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [nextBefore, setNextBefore] = useState<number | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<unknown>(null);
  const first = useLoad(async () => {
    const page = await api<{ data: AuditEntry[]; nextBefore: number | null }>(
      'GET',
      '/team/audit-log',
    );
    setEntries(page.data);
    setNextBefore(page.nextBefore);
    return page;
  }, []);

  async function loadMore() {
    if (!nextBefore) return;
    setLoadingMore(true);
    setMoreError(null);
    try {
      const page = await api<{ data: AuditEntry[]; nextBefore: number | null }>(
        'GET',
        `/team/audit-log?before=${nextBefore}`,
      );
      setEntries((list) => [...list, ...page.data]);
      setNextBefore(page.nextBefore);
    } catch (err) {
      setMoreError(err);
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <div className="card-head">
        <div>
          <h2>Audit log</h2>
          <p>Changes to API keys, domains, members, webhooks and billing, newest first.</p>
        </div>
      </div>
      <ErrorNote error={first.error ?? moreError} />
      {!first.loading && entries.length === 0 ? (
        <p className="muted" style={{ padding: '4px 20px 20px', margin: 0 }}>
          Nothing yet. Creating keys, adding domains or members shows up here.
        </p>
      ) : (
        <div className="table-wrap" style={{ padding: '12px 12px 4px' }}>
          <table className="table">
            <thead>
              <tr>
                <th>What happened</th>
                <th className="nowrap">By</th>
                <th className="nowrap hide-sm">IP address</th>
                <th className="nowrap">When</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id}>
                  <td>{describeAudit(e)}</td>
                  <td className="nowrap muted">{actorLabel(e)}</td>
                  <td className="nowrap hide-sm mono muted">{e.ip ?? '—'}</td>
                  <td className="nowrap muted" title={formatDateTime(e.createdAt)}>
                    {timeAgo(e.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {nextBefore ? (
        <div style={{ padding: '4px 20px 16px' }}>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={loadMore}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading…' : 'Show older'}
          </button>
        </div>
      ) : null}
    </div>
  );
}
